/**
 * Intervenant accounts (operator only).
 *
 * The legacy screen displayed every technician's password in a text field.
 * Here a password can be set but never read back: the server keeps only a
 * hash, and there is nothing to show.
 */
import { useState } from "react";
import { PASSWORD_MIN_LENGTH, type IntervenantView } from "@soi/contracts";
import { ApiFailure, api } from "../lib/api.js";
import { useIntervenants } from "../lib/queries.js";
import { useToast } from "../components/Toast.js";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../lib/queries.js";

function Row({ row }: { row: IntervenantView }) {
  const [draft, setDraft] = useState(row);
  const [password, setPassword] = useState("");
  const toast = useToast();
  const qc = useQueryClient();

  const save = async () => {
    try {
      await api.updateIntervenant(row.code, {
        displayName: draft.displayName,
        roleLabel: draft.roleLabel,
        email: draft.email,
        bookingsUrl: draft.bookingsUrl,
        calendarEmail: draft.calendarEmail,
        calendarId: draft.calendarId,
        ...(password ? { password } : {}),
      });
      setPassword("");
      void qc.invalidateQueries({ queryKey: queryKeys.intervenants });
      toast(`${draft.displayName} enregistré.`, "ok");
    } catch (err) {
      toast(err instanceof ApiFailure ? err.message : "L'enregistrement a échoué.", "err");
    }
  };

  const deactivate = async () => {
    try {
      await api.deactivateIntervenant(row.code);
      void qc.invalidateQueries({ queryKey: queryKeys.intervenants });
      toast(
        `${row.displayName} désactivé. Ses ${row.deliveriesCount} livraison(s) restent comptées et traçables.`,
        "annule",
      );
    } catch (err) {
      toast(err instanceof ApiFailure ? err.message : "La désactivation a échoué.", "err");
    }
  };

  return (
    <tr data-testid={`intervenant-${row.code}`} style={row.isActive ? undefined : { opacity: 0.55 }}>
      <td>
        <code>{row.code}</code>
      </td>
      <td>
        <input
          type="text"
          aria-label={`Nom · ${row.code}`}
          value={draft.displayName}
          onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
        />
      </td>
      <td>
        <input
          type="text"
          aria-label={`Rôle · ${row.code}`}
          value={draft.roleLabel}
          onChange={(e) => setDraft({ ...draft, roleLabel: e.target.value })}
        />
      </td>
      <td>
        <input
          type="email"
          aria-label={`E-mail · ${row.code}`}
          value={draft.email}
          onChange={(e) => setDraft({ ...draft, email: e.target.value })}
        />
      </td>
      <td>
        <input
          type="password"
          style={{ width: 150 }}
          placeholder="Inchangé"
          aria-label={`Nouveau mot de passe · ${row.code}`}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </td>
      <td>
        <input
          type="url"
          placeholder="https://…/bookings/"
          aria-label={`Lien Bookings · ${row.code}`}
          value={draft.bookingsUrl}
          onChange={(e) => setDraft({ ...draft, bookingsUrl: e.target.value })}
        />
      </td>
      <td>
        <input
          type="email"
          placeholder="boîte@lausanne.ch"
          aria-label={`Boîte calendrier · ${row.code}`}
          value={draft.calendarEmail}
          onChange={(e) => setDraft({ ...draft, calendarEmail: e.target.value })}
        />
      </td>
      <td>
        <input
          type="text"
          placeholder="Calendrier par défaut"
          aria-label={`Identifiant du calendrier · ${row.code}`}
          value={draft.calendarId}
          onChange={(e) => setDraft({ ...draft, calendarId: e.target.value })}
        />
      </td>
      <td>{row.deliveriesCount}</td>
      <td>
        <button type="button" className="btn small" onClick={() => void save()}>
          Enregistrer
        </button>{" "}
        {row.isActive && (
          <button type="button" className="del-btn" title="Désactiver" onClick={() => void deactivate()}>
            ✕
          </button>
        )}
      </td>
    </tr>
  );
}

export function Intervenants() {
  const { data: rows, isLoading } = useIntervenants();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: "", displayName: "", email: "", password: "" });
  const toast = useToast();
  const qc = useQueryClient();

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.createIntervenant({
        code: form.code,
        displayName: form.displayName,
        email: form.email,
        password: form.password,
        roleLabel: "",
        bookingsUrl: "",
        calendarEmail: form.email,
        calendarId: "",
      });
      setForm({ code: "", displayName: "", email: "", password: "" });
      setCreating(false);
      void qc.invalidateQueries({ queryKey: queryKeys.intervenants });
      toast("Intervenant créé.", "ok");
    } catch (err) {
      toast(err instanceof ApiFailure ? err.message : "La création a échoué.", "err");
    }
  };

  return (
    <div className="gestion">
      <div className="admin-main">
        <div className="wrap">
          <section className="panel">
            <h1>Intervenants</h1>
            <p className="lede">
              Rôles, coordonnées et lien Microsoft Bookings de chaque intervenant. Les mots de passe
              ne sont jamais affichés : seule leur empreinte est conservée côté serveur.
            </p>

            <div className="card">
              <div className="card-body" style={{ padding: 0 }}>
                <div className="tbl-wrap" style={{ border: 0, borderRadius: 0 }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Identifiant</th>
                        <th>Nom</th>
                        <th>Rôle</th>
                        <th>E-mail</th>
                        <th>Nouveau mot de passe</th>
                        <th>Lien Bookings</th>
                        <th>Boîte calendrier</th>
                        <th>Identifiant du calendrier</th>
                        <th>Livraisons</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading && (
                        <tr>
                          <td colSpan={10}>Chargement…</td>
                        </tr>
                      )}
                      {rows?.map((row) => (
                        <Row key={row.code} row={row} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="savebar">
                <span className="status" />
                {!creating && (
                  <button type="button" className="btn ghost small" onClick={() => setCreating(true)}>
                    + Ajouter un intervenant
                  </button>
                )}
              </div>
            </div>

            {creating && (
              <div className="card" style={{ marginTop: 16 }}>
                <form onSubmit={create} className="card-body">
                  <div className="toolbar">
                    <input
                      type="text"
                      placeholder="Identifiant (i5)"
                      aria-label="Identifiant"
                      value={form.code}
                      onChange={(e) => setForm({ ...form, code: e.target.value })}
                    />
                    <input
                      type="text"
                      placeholder="Nom affiché"
                      aria-label="Nom affiché"
                      value={form.displayName}
                      onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                    />
                    <input
                      type="email"
                      placeholder="prenom.nom@lausanne.ch"
                      aria-label="Adresse e-mail"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                    <input
                      type="password"
                      placeholder={`Mot de passe (${PASSWORD_MIN_LENGTH} caractères minimum)`}
                      aria-label="Mot de passe"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                    />
                    <button type="submit" className="btn small">
                      Créer
                    </button>
                    <button
                      type="button"
                      className="btn ghost small"
                      onClick={() => setCreating(false)}
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
